import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Mic, Square, ArrowLeft, Phone, Type, Clock, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecorderPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [ventType, setVentType] = useState("voice"); // "voice" or "text"
  const [isRecording, setIsRecording] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [maxDuration, setMaxDuration] = useState(120);
  const [sessionId, setSessionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [turnsRemaining, setTurnsRemaining] = useState(1);
  const [textVent, setTextVent] = useState("");
  const [canExtend, setCanExtend] = useState(true);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const isWednesdayVent = location.state?.isWednesdayVent || false;
  const persona = user?.selected_persona || "auntie";

  // Start vent session on mount
  useEffect(() => {
    const startSession = async () => {
      try {
        const response = await axios.post(
          `${API}/vent/start`,
          { persona, is_wednesday_vent: isWednesdayVent, vent_type: ventType },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSessionId(response.data.session_id);
        setMaxDuration(response.data.max_duration_seconds);
        setTimeRemaining(response.data.max_duration_seconds);
        setTurnsRemaining(response.data.turns_remaining);
      } catch (error) {
        const message = error.response?.data?.detail || "Failed to start session";
        toast.error(message);
        navigate("/persona");
      }
    };
    
    startSession();
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const extendTime = async () => {
    if (!canExtend || !sessionId) return;
    
    try {
      const response = await axios.post(
        `${API}/vent/extend/${sessionId}`,
        { additional_seconds: 60 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newDuration = response.data.new_duration;
      const additionalTime = newDuration - maxDuration;
      
      if (additionalTime > 0) {
        setMaxDuration(newDuration);
        setTimeRemaining(prev => prev + additionalTime);
        toast.success(`Added ${additionalTime} more seconds`);
      }
      
      // Check if we can extend more
      const maxTotal = user?.subscription_tier === "premium" ? 420 : 
                       user?.subscription_tier === "basic" ? 300 : 180;
      if (newDuration >= maxTotal) {
        setCanExtend(false);
      }
    } catch (error) {
      console.error("Extend error:", error);
      toast.error("Couldn't extend time");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error("Please allow microphone access to record");
    }
  };

  const stopRecording = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true);

      // Wait a bit for final data
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Process the recording
      await processVoiceRecording();
    }
  };

  const processVoiceRecording = async () => {
    try {
      const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("audio_file", audioBlob, "recording.webm");

      // Transcribe
      const transcribeResponse = await axios.post(
        `${API}/vent/transcribe/${sessionId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (transcribeResponse.data.crisis_detected) {
        navigate("/playback", { 
          state: { sessionId, crisisDetected: true, persona } 
        });
        return;
      }

      // Get AI response
      const responseData = await axios.post(
        `${API}/vent/respond/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate("/playback", {
        state: {
          sessionId,
          response: responseData.data,
          persona,
          turnsRemaining: responseData.data.turns_remaining,
        },
      });
      
    } catch (error) {
      console.error("Processing error:", error);
      toast.error("Something went wrong. Please try again.");
      setIsProcessing(false);
      setTimeRemaining(maxDuration);
    }
  };

  const submitTextVent = async () => {
    if (!textVent.trim()) {
      toast.error("Please write something first");
      return;
    }

    setIsProcessing(true);
    
    try {
      // Submit text vent
      const transcribeResponse = await axios.post(
        `${API}/vent/text/${sessionId}`,
        { text: textVent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (transcribeResponse.data.crisis_detected) {
        navigate("/playback", { 
          state: { sessionId, crisisDetected: true, persona } 
        });
        return;
      }

      // Get AI response
      const responseData = await axios.post(
        `${API}/vent/respond/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate("/playback", {
        state: {
          sessionId,
          response: responseData.data,
          persona,
          turnsRemaining: responseData.data.turns_remaining,
        },
      });
      
    } catch (error) {
      console.error("Text vent error:", error);
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = ((maxDuration - timeRemaining) / maxDuration) * 100;

  if (isProcessing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <h2 className="font-serif text-xl font-bold mb-2">
            {persona === "auntie" ? "Auntie™" : "Unk™"} is listening...
          </h2>
          <p className="text-muted-foreground">
            Preparing a response just for you
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/persona")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
          
          {isWednesdayVent && (
            <span className="text-xs bg-amber-500/20 text-amber-500 px-3 py-1 rounded-full font-medium">
              Wednesday Vent
            </span>
          )}
        </div>

        {/* Persona indicator */}
        <div className="text-center mb-6">
          <h1 className="font-serif text-2xl font-bold mb-2">
            Talk to {persona === "auntie" ? "Auntie™" : persona === "unc" ? "Unc™" : persona === "olegirl" ? "Ole Girl™" : "YK™"}
          </h1>
          <p className="text-muted-foreground text-sm">
            {ventType === "voice" 
              ? (isRecording ? "Let it out. I'm listening." : "Choose how to vent")
              : "Write what's on your mind"}
          </p>
        </div>

        {/* Vent Type Toggle */}
        <div className="flex justify-center gap-2 mb-6">
          <button
            onClick={() => setVentType("voice")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
              ventType === "voice" 
                ? "bg-primary text-primary-foreground" 
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
            data-testid="voice-mode-btn"
          >
            <Mic className="w-4 h-4" />
            Voice
          </button>
          <button
            onClick={() => setVentType("text")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
              ventType === "text" 
                ? "bg-primary text-primary-foreground" 
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
            data-testid="text-mode-btn"
          >
            <Type className="w-4 h-4" />
            Text
          </button>
        </div>

        <AnimatePresence mode="wait">
          {ventType === "voice" ? (
            <motion.div
              key="voice"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex-1 flex flex-col"
            >
              {/* Timer Circle */}
              <div className="flex-1 flex items-center justify-center">
                <div className="relative">
                  <svg className="w-64 h-64 transform -rotate-90">
                    <circle
                      cx="128"
                      cy="128"
                      r="120"
                      stroke="hsl(var(--muted))"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="128"
                      cy="128"
                      r="120"
                      stroke={isRecording ? "hsl(var(--destructive))" : "hsl(var(--primary))"}
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 120}
                      strokeDashoffset={2 * Math.PI * 120 * (1 - progress / 100)}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {isRecording ? (
                      <>
                        <div className="audio-wave mb-4">
                          <span></span>
                          <span></span>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="font-serif text-3xl font-bold">
                          {formatTime(timeRemaining)}
                        </span>
                      </>
                    ) : (
                      <span className="font-serif text-3xl font-bold text-muted-foreground">
                        {formatTime(maxDuration)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Record & Extend Buttons */}
              <div className="flex flex-col items-center gap-4 pb-6">
                <div className="flex items-center gap-4">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`record-btn ${isRecording ? "recording" : ""}`}
                    data-testid="record-btn"
                  >
                    {isRecording ? (
                      <Square className="w-8 h-8" fill="currentColor" />
                    ) : (
                      <Mic className="w-8 h-8" />
                    )}
                  </motion.button>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  {isRecording ? "Tap to stop" : "Tap to start recording"}
                </p>

                {/* Extend Time Button */}
                {canExtend && !isRecording && (
                  <button
                    onClick={extendTime}
                    className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                    data-testid="extend-time-btn"
                  >
                    <Clock className="w-4 h-4" />
                    Need more time? (+60s)
                  </button>
                )}
                
                {turnsRemaining > 1 && (
                  <p className="text-xs text-primary">
                    {turnsRemaining} turns remaining in this session
                  </p>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="text"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex-1 flex flex-col"
            >
              {/* Text Input Area */}
              <div className="flex-1 mb-6">
                <Textarea
                  value={textVent}
                  onChange={(e) => setTextVent(e.target.value)}
                  placeholder="What's on your mind? Let it all out..."
                  className="w-full h-full min-h-[300px] bg-card/50 border-border/50 rounded-2xl p-4 resize-none focus:border-primary"
                  maxLength={2000}
                  data-testid="text-vent-input"
                />
                <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                  <span>{textVent.length}/2000 characters</span>
                  {turnsRemaining > 1 && (
                    <span className="text-primary">{turnsRemaining} turns remaining</span>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={submitTextVent}
                disabled={!textVent.trim()}
                className="w-full h-14 rounded-full text-lg bg-primary hover:bg-primary/90"
                data-testid="submit-text-btn"
              >
                <Send className="w-5 h-5 mr-2" />
                Send to {persona === "auntie" ? "Auntie™" : persona === "unc" ? "Unc™" : persona === "olegirl" ? "Ole Girl™" : "YK™"}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Crisis resource footer */}
        <div className="text-center pt-4 border-t border-border/50 mt-4">
          <a
            href="tel:988"
            className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="crisis-link"
          >
            <Phone className="w-3 h-3" />
            In crisis? Call or text 988
          </a>
        </div>
      </motion.div>
    </div>
  );
}
